<!-- eslint-disable vue/no-parsing-error -->
<template>
  <div class="excel-import-container">
    <el-page-header content="基础表数据导入" />
    <el-card class="import-card" shadow="hover">
      <!-- 上传区域：保留原有逻辑，按钮禁用增加登录状态判断 -->
      <div class="upload-section">
        <el-upload
          class="excel-uploader"
          drag
          action="#"
          :auto-upload="false"
          :file-list="[]"
          :show-file-list="false"
          accept=".xlsx,.xls"
          :before-upload="beforeUpload"
          :on-change="handleFileChange"
          :on-remove="handleFileRemove"
          :limit="1"
          :on-exceed="handleExceed"
        >
          <i class="el-icon-upload2 upload-icon"></i>
          <div class="el-upload__text">
            将Excel文件拖到此处，或<em>点击上传</em>
          </div>
          <div class="el-upload__tip" slot="tip">
            仅支持.xlsx/.xls格式，文件大小不超过1000MB，当前支持16个Sheet工作表
          </div>
        </el-upload>

        <div class="custom-file-list" v-if="fileList.length > 0">
          <div class="file-item">
            <i class="el-icon-file-excel file-icon"></i>
            <span class="file-name" :title="fileList[0].name">{{ fileList[0].name }}</span>
            <el-button
              type="text"
              icon="el-icon-delete"
              class="delete-btn"
              @click="handleFileRemove"
            ></el-button>
          </div>
        </div>

        <div class="btn-group" v-if="fileList.length > 0">
          <el-button
            type="primary"
            icon="el-icon-read"
            @click="parseExcel"
            :loading="isParsing"
            :disabled="!hasToken || isParsing || isImporting"
          >
            解析Excel数据
          </el-button>
          <el-button
            type="success"
            icon="el-icon-upload"
            @click="submitImport"
            :loading="isImporting"
            :disabled="!hasToken || !parsedData.length || isParsing || isImporting"
          >
            提交导入
          </el-button>
          <el-progress 
            v-if="isParsing && parseProgress > 0" 
            :percentage="parseProgress" 
            size="small" 
            style="width: 200px; margin-left: 20px;"
          />
        </div>
      </div>

      <div class="preview-section" v-if="parsedData.length > 0">
        <el-divider content-position="left">
          <i class="el-icon-data-analysis"></i> 数据预览（共{{ parsedData.length }}个Sheet，点击Sheet名称切换）
        </el-divider>
        <el-tabs v-model="activeSheet" type="card" class="sheet-tabs" @tab-click="handleTabChange">
          <el-tab-pane
            v-for="(item, index) in parsedData"
            :key="index"
            :label="`${item.originalSheetName}（${item.data.length}条）`"
          >
            <el-table
              :data="currentPageData"
              border
              stripe
              size="small"
              max-height="400"
              v-loading="isTableLoading"
              :row-key="(row, idx) => idx"
              :highlight-current-row="false"
            >
              <el-table-column
                v-for="(prop, propIndex) in Object.keys(parsedData[activeSheet].data[0] || {})"
                :key="propIndex"
                :prop="prop"
                :label="prop"
                align="center"
                :width="Math.min(Math.max(prop.length * 8 + 40, 80), 250)"
                show-overflow-tooltip
              />
            </el-table>

            <div class="pagination-container" v-if="parsedData[activeSheet].data.length > 0">
              <el-pagination
                @size-change="handleSizeChange"
                @current-change="handleCurrentChange"
                :current-page="pagination.currentPage"
                :page-sizes="[50, 100, 200, 500]"
                :page-size="pagination.pageSize"
                layout="total, sizes, prev, pager, next, jumper"
                :total="parsedData[activeSheet].data.length"
                background
                small
              />
            </div>

            <div class="data-count-tip" v-if="parsedData[activeSheet].data.length > 1000">
              <i class="el-icon-warning"></i> 当前Sheet数据量较大（{{ parsedData[activeSheet].data.length }}条），已分页展示，可直接提交导入
            </div>
            <div class="data-count-tip info" v-else-if="parsedData[activeSheet].data.length > 500">
              <i class="el-icon-info"></i> 当前Sheet数据量较多（{{ parsedData[activeSheet].data.length }}条），分页展示更流畅，建议直接提交导入
            </div>
          </el-tab-pane>
        </el-tabs>
      </div>
    </el-card>
  </div>
</template>

<script>
import service from '@/utils/request'
import { fixExcelDate } from '@/utils/date'
import * as XLSX from 'xlsx'
import router from '@/router' // 引入路由，用于登录跳转

export default {
  name: 'ExcelImport',
  data() {
    return {
      fileList: [],
      rawFile: null,
      isParsing: false,
      isImporting: false,
      isTableLoading: false,
      parseProgress: 0,
      parsedData: [],
      activeSheet: 0,
      pagination: {
        currentPage: 1,
        pageSize: 100
      },
      currentPageData: []
    }
  },
  // 新增：计算属性，实时判断本地是否有有效token（响应式）
  computed: {
    hasToken() {
      const token = localStorage.getItem('accessToken')
      // 校验token存在且非空字符串
      return !!token && token.trim() !== ''
    }
  },
  watch: {
    activeSheet: {
      handler() {
        this.resetPagination()
        this.setCurrentPageData()
      },
      immediate: true
    },
    pagination: {
      handler() {
        this.setCurrentPageData()
      },
      deep: true
    },
    // 新增：监听token变化，若token失效则清空解析数据，阻止后续操作
    hasToken(newVal) {
      if (!newVal) {
        this.parsedData = []
        this.fileList = []
        this.rawFile = null
      }
    }
  },
  methods: {
    // 新增：通用登录状态校验方法（可复用）
    checkLoginStatus() {
      if (!this.hasToken) {
        this.$message.error('登录状态已过期，请重新登录！')
        // 跳转到登录页，携带当前页面地址，登录后可返回
        router.push({
          path: '/login',
          query: { redirect: this.$route.fullPath }
        })
        return false
      }
      return true
    },

    // 原有方法：上传前校验
    beforeUpload(file) {
      const fileType = file.name.split('.').pop().toLowerCase()
      const isExcel = fileType === 'xlsx' || fileType === 'xls'
      const isLt1000M = file.size / 1024 / 1024 < 1000

      if (!isExcel) {
        this.$message.error('仅支持上传 .xlsx / .xls 格式的Excel文件！')
        return false
      }
      if (!isLt1000M) {
        this.$message.error('Excel文件大小不能超过1000MB！')
        return false
      }
      return true
    },

    // 原有方法：文件变更
    handleFileChange(file, fileList) {
      this.fileList = fileList.slice(-1)
      this.rawFile = this.fileList[0]?.raw || null
      this.parsedData = []
      this.activeSheet = 0
      this.parseProgress = 0
      this.resetPagination()
    },

    // 原有方法：移除文件
    handleFileRemove() {
      this.fileList = []
      this.rawFile = null
      this.parsedData = []
      this.activeSheet = 0
      this.parseProgress = 0
      this.resetPagination()
      this.$message.info('已移除选择的Excel文件')
    },

    // 原有方法：超出文件数量限制提示
    handleExceed() {
      this.$message.warning('仅支持上传1个Excel文件，请先移除已选择的文件！')
    },

    // 修复：解析Excel前增加登录状态校验
    async parseExcel() {
      // 第一步：校验登录状态，失败则直接返回
      if (!this.checkLoginStatus()) return
      if (!this.rawFile) {
        this.$message.warning('请先选择要解析的Excel文件！')
        return
      }

      this.isParsing = true
      this.parseProgress = 0
      try {
        const arrayBuffer = await this.readFileAsArrayBuffer(this.rawFile)
        const workbook = (this.$XLSX || XLSX).read(arrayBuffer, {
          type: 'array',
          cellDates: true, // 必须开启，转Date对象
          cellText: false,
          dateNF: 'yyyy-mm-dd', // 小写mm，避免XLSX.js自动格式化
          cellStyles: false,
          sheetStubs: false,
          raw: false // 必须false，否则cellDates失效
        })

        const sheetNames = workbook.SheetNames
        const requiredSheetCount = 16
        if (sheetNames.length !== requiredSheetCount) {
          this.$message.error(`解析失败！当前Excel包含${sheetNames.length}个Sheet，要求必须为${requiredSheetCount}个！`)
          this.isParsing = false
          return
        }

        const parsedList = []
        const totalSheets = sheetNames.length
        for (let index = 0; index < totalSheets; index++) {
          const sheetName = sheetNames[index]
          const worksheet = workbook.Sheets[sheetName]
          const sheetData = (this.$XLSX || XLSX).utils.sheet_to_json(worksheet, {
            header: 1,
            defval: '',
            blankrows: false
          })

          const header = (sheetData[0] || []).map((item, hIndex) => {
            return item || `未命名列${hIndex + 1}`
          })

          const data = sheetData.slice(1).map((row) => {
            const rowObj = {}
            header.forEach((key, colIndex) => {
              let value = row[colIndex] || ''
              if (value instanceof Date) {
                 value=fixExcelDate(value,true)               
              }
              rowObj[key] = value
            })
            return rowObj
          })

          parsedList.push({
            originalSheetName: sheetName,
            sheetName: `Sheet${index + 1}`,
            data: data
          })

          this.parseProgress = Math.round(((index + 1) / totalSheets) * 100)
          await new Promise(resolve => requestAnimationFrame(resolve))
        }

        this.parsedData = parsedList
        this.resetPagination()
        this.$message.success(`解析成功！共解析${parsedList.length}个Sheet，可预览数据后提交导入！`)
      } catch (error) {
        this.$message.error(`Excel解析失败：${error.message || '文件损坏、格式异常或正在被占用'}`)
        console.error('Excel解析异常：', error)
      } finally {
        this.isParsing = false
        this.parseProgress = 0
      }
    },

    // 原有方法：File转ArrayBuffer
    readFileAsArrayBuffer(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => resolve(e.target.result)
        reader.onerror = () => reject(new Error('Excel文件读取失败，请检查文件是否损坏、未关闭或正在被占用'))
        reader.onabort = () => reject(new Error('文件读取被中断'))
        reader.readAsArrayBuffer(file)
      })
    },

    // 修复：提交导入前增加登录状态校验，强化异常处理
    async submitImport() {
      // 第一步：校验登录状态，失败则直接返回（核心修复）
      if (!this.checkLoginStatus()) return
      if (this.parsedData.length === 0) {
        this.$message.warning('请先解析Excel数据再提交！')
        return
      }

      try {
        const totalDataCount = this.parsedData.reduce((total, item) => total + item.data.length, 0)
        const confirmRes = await this.$confirm(
          `确定要提交这16个Sheet的基础数据导入吗？共${totalDataCount}条数据，导入后将不可撤回！`,
          '导入确认',
          {
            confirmButtonText: '确定导入',
            cancelButtonText: '取消',
            type: 'warning',
            confirmButtonClass: 'el-button--danger'
          }
        )
        if (confirmRes !== 'confirm') return

        this.isImporting = true
        this.isTableLoading = true
        const submitData = {
          excelName: this.fileList[0].name,
          sheetDatas: this.parsedData.map(item => ({
            sheetIndex: this.parsedData.findIndex(v => v.sheetName === item.sheetName) + 1,
            originalSheetName: item.originalSheetName,
            sheetName: item.sheetName,
            data: item.data
          }))
        }
        console.log('准备提交导入数据：', submitData)
        const res = await service.post('excel/import', submitData, {
          timeout: 3000000,
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded / (progressEvent.total || 1)) * 100)
            console.log('上传进度：', percent, '%')
          }
        })

        if (res && res.code === 200) {
          this.$message.success('基础表数据导入成功！')
          this.handleFileRemove()
        } else {
          this.$message.error(`导入失败：${res?.msg || '后端处理失败，未返回有效结果'}`)
        }
      } catch (error) {
        if (error === 'cancel' || (error.name === 'Error' && error.message === 'cancel')) {
          this.$message.info('已取消数据导入')
        } else {
          // 过滤全局401拦截的错误，避免重复提示
          if (error.message !== '登录状态失效，请重新登录') {
            this.$message.error(`导入请求失败：${error.message || '网络异常、请求超时或后端接口未响应，请检查后端服务'}`)
            console.error('Excel导入请求异常详情：', error)
          }
        }
      } finally {
        this.isImporting = false
        this.isTableLoading = false
      }
    },

    // 原有分页方法：重置分页参数
    resetPagination() {
      this.pagination = {
        currentPage: 1,
        pageSize: 100
      }
    },

    // 原有分页方法：计算并设置当前页数据
    setCurrentPageData() {
      if (!this.parsedData.length || !this.parsedData[this.activeSheet]?.data.length) {
        this.currentPageData = []
        return
      }
      const { currentPage, pageSize } = this.pagination
      const startIndex = (currentPage - 1) * pageSize
      const endIndex = currentPage * pageSize
      this.currentPageData = this.parsedData[this.activeSheet].data.slice(startIndex, endIndex)
    },

    // 原有分页方法：切换单页展示条数
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.pagination.currentPage = 1
    },

    // 原有分页方法：切换当前页码
    handleCurrentChange(val) {
      this.pagination.currentPage = val
    },

    // 原有方法：Sheet标签点击事件
    handleTabChange(tab) {
      this.activeSheet = Number(tab.index)
    }
  }
}
</script>

<style scoped>
.excel-import-container {
  padding: 20px;
  background-color: #f5f5f5;
  min-height: calc(100vh - 120px);
}

.import-card {
  padding: 30px;
}

.upload-section {
  margin-bottom: 30px;
}

/* 拖拽上传区域样式 */
.excel-uploader {
  width: 100%;
  height: 200px;
  line-height: 200px;
  text-align: center;
  border: 2px dashed #dcdcdc;
  border-radius: 6px;
  margin-bottom: 20px;
  cursor: pointer;
  transition: all 0.3s;
}
.excel-uploader:hover {
  border-color: #409eff;
  background-color: #f8f9fa;
}
.upload-icon {
  font-size: 48px;
  color: #409eff;
  margin-bottom: 10px;
  display: block;
  line-height: 1;
}

/* 自定义文件列表样式 */
.custom-file-list {
  width: 100%;
  margin-bottom: 20px;
}
.file-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 15px;
  border: 1px solid #e6e6e6;
  border-radius: 6px;
  background-color: #fff;
}
.file-icon {
  font-size: 20px;
  color: #67c23a;
  margin-right: 12px;
}
.file-name {
  flex: 1;
  font-size: 14px;
  color: #333;
  white-space: nowrap;
  overflow-x: auto;
  text-overflow: ellipsis;
  max-height: 20px;
  padding: 0 2px;
}
.file-name::-webkit-scrollbar {
  height: 4px;
}
.file-name::-webkit-scrollbar-thumb {
  background-color: #dcdcdc;
  border-radius: 2px;
}
.file-name::-webkit-scrollbar-track {
  background-color: #f5f5f5;
}
.delete-btn {
  color: #f56c6c;
  font-size: 16px;
}
.delete-btn:hover {
  color: #f04848;
}

/* 操作按钮组 */
.btn-group {
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
}
.btn-group .el-button {
  margin: 0 10px 10px;
}

/* 预览区域样式 */
.preview-section {
  margin-top: 20px;
}
.sheet-tabs {
  margin-bottom: 15px;
}

/* 分页组件容器样式 */
.pagination-container {
  margin-top: 15px;
  text-align: right;
}

/* 数据量提示样式 */
.data-count-tip {
  margin-top: 10px;
  padding: 8px 12px;
  background-color: #fff2f2;
  border: 1px solid #ffccc7;
  border-radius: 4px;
  color: #f56c6c;
  font-size: 12px;
  display: flex;
  align-items: center;
}
.data-count-tip.info {
  background-color: #fffbee;
  border-color: #ffe58f;
  color: #fa8c16;
}
.data-count-tip .el-icon-info,
.data-count-tip .el-icon-warning {
  margin-right: 8px;
}
</style>